import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  Button,
  Card,
  CardDescription,
  CardTitle,
  EmptyState,
  FormAlert,
  PageHeader,
  Skeleton,
  buttonVariants,
  cn,
  formatNumber,
} from '@mgdlead/ui';
import { DatabaseBackup, Download, LogOut, Lock, SlidersHorizontal, Users } from 'lucide-react';
import { can } from '@mgdlead/shared';

import { BaixarContatosDialog } from '../../../features/contatos/BaixarContatosDialog';
import {
  useContactsExportHistory,
  useWorkspaceContactsPreview,
  workspaceContactsExportUrl,
  type ContactsExportHistory,
  type ContatosFiltro,
} from '../../../features/contatos/contatos-export';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

/**
 * Contatos — UM lugar para baixar listas e fazer backup (pedido do dono, 11/10: "meio ruim
 * achar os caminhos"). Antes, o download vivia num botão da tela de Grupos e no detalhe de
 * cada grupo; quem queria "o backup" tinha que saber onde procurar.
 *
 * A tela não inventa exportação nova: são atalhos para a MESMA rota de contatos, com os
 * filtros já escolhidos, e o histórico vem da auditoria que o download já grava.
 */

const BACKUP: ContatosFiltro = { groupIds: null, situacao: 'todos', admins: 'incluir' };
const NO_GRUPO: ContatosFiltro = { groupIds: null, situacao: 'no_grupo', admins: 'excluir' };
const SAIRAM: ContatosFiltro = { groupIds: null, situacao: 'saiu', admins: 'incluir' };

/** Backup mais velho que isso ganha o lembrete. */
const SEMANA_MS = 7 * 24 * 60 * 60 * 1000;

type Item = ContactsExportHistory[number];

function pessoas(n: number) {
  return `${formatNumber(n, { abbreviate: 'never' })} ${n === 1 ? 'pessoa' : 'pessoas'}`;
}

function ehBackupCompleto(h: Item) {
  return h.scope === 'workspace' && h.allGroups && h.situacao === 'todos' && h.admins === 'incluir';
}

function descreve(h: Item): string {
  if (h.scope === 'group') return `Grupo ${h.groupName ?? 'sem nome'}`;
  if (ehBackupCompleto(h)) return 'Backup completo';
  const grupos = h.allGroups
    ? 'Todos os grupos'
    : `${h.groupsCount ?? '?'} ${h.groupsCount === 1 ? 'grupo' : 'grupos'}`;
  const quem =
    h.situacao === 'no_grupo' ? 'quem está' : h.situacao === 'saiu' ? 'quem saiu' : 'todos';
  return `${grupos} · ${quem}${h.admins === 'excluir' ? ', sem administradores' : ''}`;
}

export function ContatosPage() {
  const { workspace, role } = useWorkspace();
  useDocumentTitle(`Contatos · ${workspace.name}`);
  const pode = can(role, 'group:export_contacts');
  const [sobMedida, setSobMedida] = useState(false);
  const queryClient = useQueryClient();

  const backup = useWorkspaceContactsPreview(workspace.id, BACKUP, pode);
  const noGrupo = useWorkspaceContactsPreview(workspace.id, NO_GRUPO, pode);
  const sairam = useWorkspaceContactsPreview(workspace.id, SAIRAM, pode);
  const historico = useContactsExportHistory(workspace.id, pode);

  if (!pode) {
    return (
      <>
        <PageHeader title="Contatos" />
        <EmptyState
          kind="no-data"
          icon={Lock}
          title="Você não tem permissão para baixar contatos"
          description="A lista leva o telefone das pessoas. Peça a quem administra o workspace."
        />
      </>
    );
  }

  /** O rastro é gravado depois que o arquivo termina de sair: relê em seguida. */
  const aposBaixar = () => {
    for (const ms of [1500, 5000]) {
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: [['group']] }), ms);
    }
  };

  const ultimoBackup = historico.data?.find(ehBackupCompleto) ?? null;
  const backupVelho =
    historico.isSuccess &&
    (ultimoBackup === null || Date.now() - new Date(ultimoBackup.at).getTime() > SEMANA_MS);

  return (
    <>
      <PageHeader
        title="Contatos"
        description="Baixe a lista de contatos dos seus grupos ou guarde um backup de todo mundo."
      />

      <Card className="mb-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <DatabaseBackup aria-hidden="true" className="size-5" />
            </span>
            <div className="min-w-0">
              <CardTitle>Backup completo</CardTitle>
              <CardDescription>
                Todo mundo que já passou pelos seus grupos — quem está e quem saiu — numa planilha
                só, com telefone, grupo e DDD.
              </CardDescription>
              <p className="nums mt-2 text-sm text-foreground">
                {backup.data ? (
                  <>
                    <span className="font-semibold">{pessoas(backup.data.people)}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      em {formatNumber(backup.data.groups)}{' '}
                      {backup.data.groups === 1 ? 'grupo' : 'grupos'}
                    </span>
                  </>
                ) : (
                  <Skeleton className="h-4 w-40" />
                )}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {historico.isPending
                  ? ' '
                  : ultimoBackup
                    ? `Último backup ${formatRelative(ultimoBackup.at) ?? ''}${ultimoBackup.by ? ` por ${ultimoBackup.by}` : ''}.`
                    : 'Você ainda não baixou nenhum backup completo.'}
              </p>
            </div>
          </div>
          <a
            href={workspaceContactsExportUrl(workspace.id, BACKUP)}
            download
            onClick={aposBaixar}
            className={cn(buttonVariants({ variant: 'primary' }), 'shrink-0 gap-1.5')}
          >
            <Download aria-hidden="true" className="size-4" />
            Baixar backup completo
          </a>
        </div>
        {backupVelho ? (
          <FormAlert tone="info" className="mt-4">
            Vale baixar um backup por semana: se um grupo for banido, a lista de contatos continua
            com você.
          </FormAlert>
        ) : null}
      </Card>

      <h2 className="mb-2 text-sm font-medium text-muted-foreground">Listas prontas</h2>
      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <Atalho
          icon={Users}
          titulo="Quem está nos grupos"
          texto="Só os membros que estão hoje, sem os administradores."
          total={noGrupo.data?.people}
          href={workspaceContactsExportUrl(workspace.id, NO_GRUPO)}
          onBaixar={aposBaixar}
        />
        <Atalho
          icon={LogOut}
          titulo="Quem saiu dos grupos"
          texto="Para chamar de volta ou convidar para um grupo novo."
          total={sairam.data?.people}
          href={workspaceContactsExportUrl(workspace.id, SAIRAM)}
          onBaixar={aposBaixar}
        />
        <Card size="sm" className="flex flex-col">
          <SlidersHorizontal aria-hidden="true" className="size-5 text-primary" />
          <p className="mt-2 text-sm font-semibold">Lista sob medida</p>
          <p className="mt-1 flex-1 text-xs text-muted-foreground">
            Escolha os grupos, quem está ou quem saiu, com ou sem administradores.
          </p>
          <Button variant="secondary" className="mt-3" onClick={() => setSobMedida(true)}>
            Montar lista
          </Button>
        </Card>
      </div>

      <Card>
        <CardTitle>Últimos downloads</CardTitle>
        {historico.isPending ? (
          <Skeleton className="mt-3 h-16 w-full" />
        ) : (historico.data?.length ?? 0) === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">Nenhuma lista baixada ainda.</p>
        ) : (
          <ul className="mt-2 divide-y divide-border">
            {historico.data!.map((h, i) => (
              <li
                key={`${String(h.at)}-${i}`}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2.5 text-sm"
              >
                <span className="min-w-0 text-foreground">{descreve(h)}</span>
                <span className="nums text-xs text-muted-foreground">
                  {h.rows !== null ? `${formatNumber(h.rows)} linhas · ` : ''}
                  {formatExact(h.at, workspace.timezone)}
                  {h.by ? ` · ${h.by}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Para baixar um grupo só, abra o grupo em{' '}
          <Link
            className="text-primary underline-offset-2 hover:underline"
            to={workspacePath(workspace.slug, 'grupos')}
          >
            Grupos
          </Link>
          .
        </p>
      </Card>

      <BaixarContatosDialog
        open={sobMedida}
        onOpenChange={(o) => {
          setSobMedida(o);
          if (!o) aposBaixar();
        }}
        workspaceId={workspace.id}
        allowed={pode}
      />
    </>
  );
}

function Atalho({
  icon: Icon,
  titulo,
  texto,
  total,
  href,
  onBaixar,
}: {
  icon: typeof Users;
  titulo: string;
  texto: string;
  total: number | undefined;
  href: string;
  onBaixar: () => void;
}) {
  return (
    <Card size="sm" className="flex flex-col">
      <Icon aria-hidden="true" className="size-5 text-primary" />
      <p className="mt-2 text-sm font-semibold">{titulo}</p>
      <p className="mt-1 flex-1 text-xs text-muted-foreground">{texto}</p>
      <p className="nums mt-2 text-sm font-semibold">
        {total === undefined ? <Skeleton className="h-4 w-24" /> : pessoas(total)}
      </p>
      <a
        href={href}
        download
        onClick={onBaixar}
        aria-label={`Baixar: ${titulo}`}
        className={cn(buttonVariants({ variant: 'secondary' }), 'mt-3 gap-1.5')}
      >
        <Download aria-hidden="true" className="size-4" />
        Baixar
      </a>
    </Card>
  );
}
